import { PROJECT_ACCESS_OPTIONS } from 'Common/Constants/Constants'
import { format, addMinutes } from 'date-fns'
import { Project } from 'Features/Project/ProjectList/ProjectList.types'
import { mkConfig, generateCsv, asBlob, AcceptedData } from 'export-to-csv'

const csvConfig = mkConfig({ useKeysAsHeaders: true })

export const convertToTitleCase = (str: string) => {
  return str.charAt(0).toUpperCase() + str.toLowerCase().slice(1)
}

export const capitalize = (str: string) => {
  return str.charAt(0).toUpperCase() + str.slice(1)
}

export const replaceUnderScoreWithSpace = (str: string) => {
  return str.replace(/_/gi, ' ')
}

export const formatDateToUTC = (dateString: string) => {
  const date = new Date(dateString)
  return `${format(
    addMinutes(date, date.getTimezoneOffset()),
    'dd/MM/yyyy - HH:mm'
  )} UTC`
}

export const mapDropdownMenuItemsInLabel = (
  items: { id: number; label: string }[]
) => {
  return items.map((item) => {
    return {
      key: item.label,
      value: item.id,
    }
  })
}

export const mapDropdownMenuItemsInName = (
  items: { id: number; label: string; name: string }[]
) => {
  return items.map((item) => {
    return {
      key: item.name,
      value: item.id,
    }
  })
}

export const getDropdownNameFromId = (
  items: { id: number; label: string; name: string }[],
  id: number
) => {
  const name = items
    .filter((item) => item.id === id)
    .map((item) => item.name)[0]
  return name ? name : ''
}

export const getLookUpLabelFromId = (
  items: { id: number; label: string }[],
  id: number
) => {
  return items.filter((item) => item.id === id).map((item) => item.label)[0]
}

export const getLookUpIdFromLabel = (
  items: { id: number; label: string }[],
  label: string
) => {
  return items.filter((item) => item.label === label).map((item) => item.id)[0]
}

export const isInteger = (num: number) => {
  return num % 1 === 0
}

export const generatedBySelect = (
  showPlansOption: string,
  projectData: Project[],
  userEmail: string | null,
  setProjectTableData: (data: Project[]) => void
) => {
  if (showPlansOption === PROJECT_ACCESS_OPTIONS['Generated by me']) {
    const myPlans: Project[] = projectData.filter(
      (row: Project) => row.email === userEmail
    )
    setProjectTableData(myPlans)
  } else if (
    showPlansOption === PROJECT_ACCESS_OPTIONS['Not generated by me']
  ) {
    const myPlans: Project[] = projectData.filter(
      (row: Project) => row.email !== userEmail
    )
    setProjectTableData(myPlans)
  } else if (
    showPlansOption === PROJECT_ACCESS_OPTIONS['Generated by anyone']
  ) {
    setProjectTableData(projectData)
  }
}

export const getTableHeight = (el?: HTMLDivElement) => {
  const windowHeight = window.innerHeight
  const footerHeight = 60
  const adjustment = 40
  let topOftheTable = 0
  if (!el) {
    topOftheTable =
      document.getElementById('table-container-mui')?.getBoundingClientRect()
        .top ?? 280
  } else {
    topOftheTable = el?.getBoundingClientRect().top ?? 280
  }

  const heightOftheTable =
    windowHeight - footerHeight - adjustment - topOftheTable
  return heightOftheTable < 300 ? 300 : heightOftheTable
}

export const autoHeight = (el: HTMLDivElement, containerId?: string) => {
  const heightOftheTable = getTableHeight(el)
  document
    .getElementById(containerId ?? '#table-container-mui')
    ?.style.setProperty(
      'height',
      `${Math.floor(heightOftheTable < 300 ? 300 : heightOftheTable)}px`
    )
}

export const downloadAsCsv = (
  fileName: string,
  dataToDownload: Record<string, AcceptedData>[]
) => {
  const csv = generateCsv(csvConfig)(dataToDownload)

  // Generate the Blob from the CsvOutput
  const blob = asBlob(csvConfig)(csv)

  // Requires URL to be available (web workers or client scripts only)
  const link = document.createElement('a')
  link.href = URL.createObjectURL(blob)
  link.download = `${new Date().toISOString()}-${fileName}.csv`
  link.click()
}

// Set of common abbreviations that should remain uppercase
const PRESERVE_UPPERCASE = new Set([
  'SKU',
  'MOQ',
  'ID',
  'API',
  'URL',
  'CSV',
  'PDF',
  'XML',
  'JSON',
  'HTML',
  'CSS',
  'JS',
  'SQL',
  'BOM',
  'ERP',
  'WMS',
  'MRP',
  'SOP',
  'KPI',
])
/**
 * Converts text to sentence case with proper handling of abbreviations
 * @param text - The text to convert
 * @returns Text in sentence case with preserved abbreviations
 */
export const toSentenceCase = (text: string): string => {
  if (!text) return text

  // Split by spaces and process each word
  const words = text.split(' ')
  const result = words.map((word, index) => {
    // Check if word is a preserved abbreviation (case-insensitive check)
    const upperWord = word.toUpperCase()
    if (PRESERVE_UPPERCASE.has(upperWord)) {
      return upperWord
    }

    // First word gets capitalized, others get lowercased unless they're abbreviations
    if (index === 0) {
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
    } else {
      return word.toLowerCase()
    }
  })

  return result.join(' ')
}

export const setTableContainerHeight = (el: HTMLDivElement, height: number) => {
  if (!el) return
  el.closest('.table-container-mui')?.style.setProperty('height', `${height}px`)
}
